import { useEffect, useState } from 'react';
import { ColumnFilter, Filter } from './DataGridFilterTypes';

export type InternalState = {
  [key: string]: Filter | undefined;
};

export type UseDataGridFilterMenuStateProps = {
  filters: ColumnFilter[];
};

export type UseDataGridFilterMenuStateReturnValue = {
  filterState: InternalState;
  filterOrder: string[];
  addFilter: () => void;
  removeFilter: (filterId: string) => void;
  getColumnFilters: () => ColumnFilter[];
  changeFilterValue: (filterId: string, filterValue: Filter) => void;
  changeFilterColumn: (oldFilterId: string, newColumnId: string) => void;
  reset: () => void;
  initialize: (filters: ColumnFilter[]) => void;
};

const makeEmptyFilter = (columnId?: string | null): Filter => {
  return {
    columnId: columnId || null,
    operator: null,
    value: null,
  };
};

const getNextFilterNumber = (filterOrder: string[], columnId: string | null) => {
  // find the existing filters for the new columnId
  // get the number suffix, parse as string, add one.
  let number = 0;
  const filterIdPrefix = `${columnId}-`;

  filterOrder
    .filter((filterId) => filterId.includes(filterIdPrefix))
    .forEach((filterId) => {
      const filterIdCount = Number(filterId.replace(filterIdPrefix, ''));
      if (number < filterIdCount) {
        number = filterIdCount;
      }
    });
  return number + 1;
};

/**
 * Converts the react-table filters array into an object like:
 * {
 *   column1-1: filterValue1,
 *   column1-2: filterValue2,
 *   ...etc
 * }
 * @param columns Column instances
 * @param filters initial state filters or state.filters
 * @returns
 */
const initializeFilterState = (filters: ColumnFilter[]): InternalState => {
  const finalState: Record<string, Filter | undefined> = {};

  filters.forEach((filter: ColumnFilter) => {
    if (filter?.value?.length) {
      filter.value.forEach((filterValue, id) => {
        finalState[`${filter.id}-${id}`] = filterValue;
      });
    }
  });
  return finalState;
};

/**
 * removes a SINGLE filter from a column's existing filterValue array
 */
const removeColumnFilter = (
  filterId: string,
  internalState: InternalState,
  filterOrder: string[],
): [InternalState, string[]] => {
  const newInternalState = { ...internalState };
  delete newInternalState[filterId];
  const newFilterOrder = [...filterOrder].filter((filterOrderId) => {
    return filterId !== filterOrderId;
  });

  return [newInternalState, newFilterOrder];
};

/**
 * Adds a SINGLE filter to a column's existing filterValue array
 */
const addColumnFilter = (
  internalState: InternalState,
  filterOrder: string[],
  columnId: string | null,
): [InternalState, string[]] => {
  // generate new filterId
  const newFilterId = `${columnId}-${getNextFilterNumber(filterOrder, columnId)}`;

  // update order for adding
  // update filter order
  const newFilterOrder = [...filterOrder];
  newFilterOrder.push(newFilterId);

  // return newly modified internal state and filterId
  return [
    {
      ...internalState,
      [newFilterId]: makeEmptyFilter(columnId),
    },
    newFilterOrder,
  ];
};

/**
 * Handles situation where you change a column selection from one type to another.
 * Must change the column and maintain order.
 */
const changeColumnFilter = (
  oldFilterId: string,
  newColumnId: string,
  filterState: InternalState,
  filterOrder: string[],
): [InternalState, string[]] => {
  const newFilterState = { ...filterState };

  // generate new filterId
  const newFilterId = `${newColumnId}-${getNextFilterNumber(filterOrder, newColumnId)}`;

  // remove old filter
  delete newFilterState[oldFilterId];

  // add new blank filter
  newFilterState[newFilterId] = makeEmptyFilter(newColumnId);

  // update filter order inline
  const newFilterOrder = filterOrder.map((filterId) => {
    if (filterId === oldFilterId) {
      return newFilterId;
    }
    return filterId;
  });

  return [newFilterState, newFilterOrder];
};

/**
 * Removes all filters from the filter state. Used by the 'reset' button.
 */
const removeAllFilters = (): [InternalState, string[]] => {
  const baseState = initializeFilterState([]);
  const [newState, newFilterOrder] = addColumnFilter(baseState, [], null);
  return [newState, newFilterOrder];
};

/**
 * Converts filterstate back into an array of column filters that's consumable by react-table
 */
const toColumnFilters = (filterState: InternalState): ColumnFilter[] => {
  return Object.keys(filterState).reduce((converted: ColumnFilter[], filterId) => {
    const columnId = filterId.split('-')[0];
    const filter = filterState[filterId];

    // create a ColumnFilter for is Empty or is not Empty
    if (filter?.operator === 'isEmpty' || (filter?.operator === 'isNotEmpty' && !filter.value)) {
      const existingColumnFilter: ColumnFilter | undefined = converted.find((columnFilter: ColumnFilter) => {
        return columnFilter.id === columnId;
      });

      if (!existingColumnFilter) {
        const newFilter: Filter = {
          columnId,
          operator: filter?.operator,
          value: ' ',
        };
        const newColumnFilter: ColumnFilter = { id: columnId, value: [newFilter as Filter] };
        converted.push(newColumnFilter);
      } else {
        existingColumnFilter?.value?.push(filterState[filterId] as Filter);
      }
      return converted;
    }

    // if the filter is invalid, skip it.
    if (!filter?.columnId || !filter?.operator || !filter?.value) {
      return converted;
    }

    // find an existing ColumnFilter
    const existingColumnFilter: ColumnFilter | undefined = converted.find((columnFilter: ColumnFilter) => {
      return columnFilter.id === columnId;
    });

    // create a new ColumnFilter or add to an existing one
    if (!existingColumnFilter) {
      const newColumnFilter: ColumnFilter = { id: columnId, value: [filterState[filterId] as Filter] };
      converted.push(newColumnFilter);
    } else {
      existingColumnFilter?.value?.push(filterState[filterId] as Filter);
    }
    return converted;
  }, [] as ColumnFilter[]);
};

/**
 * Updates the value of a single filter inline, does not change keys or order.
 */
const updateFilterValue = (filterId: string, filterValue: Filter, filterState: InternalState): InternalState => {
  const newFilterState = { ...filterState };
  newFilterState[filterId] = filterValue;

  return newFilterState;
};

/**
 * This is the actual hook used to manage the filter state.
 */
const useDataGridFilterMenuState = ({
  filters,
}: UseDataGridFilterMenuStateProps): UseDataGridFilterMenuStateReturnValue => {
  const [filterState, setFilterState] = useState(initializeFilterState(filters)); // { column1-1: value, column2-1: value}
  const [filterOrder, setFilterOrder] = useState(Object.keys(filterState)); // [column1-1, column1-2, column2-1]

  useEffect(() => {
    if (!filterOrder.length) {
      const [newState, newFilterOrder] = addColumnFilter(filterState, filterOrder, null);
      setFilterState(newState);
      setFilterOrder(newFilterOrder);
    }
  }, [filterOrder]);
  /**
   * Adds a new filter for an unknown column into the state
   */
  const addFilter = () => {
    const [newState, newFilterOrder] = addColumnFilter(filterState, filterOrder, null);
    setFilterState(newState);
    setFilterOrder(newFilterOrder);
  };

  /**
   * Removes the desired filter by filter ID (filter ID's are generated internally)
   * @param filterId
   */
  const removeFilter = (filterId: string) => {
    const [newState, newFilterOrder] = removeColumnFilter(filterId, filterState, filterOrder);
    setFilterState(newState);
    setFilterOrder(newFilterOrder);
  };

  /**
   * Changes an existing filter to a different chosen column
   * @param oldFilterId The filterID you want to remove
   * @param newColumnId the ID of the column you're changing the filter to
   */
  const changeFilterColumn = (oldFilterId: string, newColumnId: string) => {
    const [newState, newFilterOrder] = changeColumnFilter(oldFilterId, newColumnId, filterState, filterOrder);
    setFilterState(newState);
    setFilterOrder(newFilterOrder);
  };

  const changeFilterValue = (filterId: string, filterValue: Filter) => {
    const newState = updateFilterValue(filterId, filterValue, filterState);
    setFilterState(newState);
  };

  const reset = () => {
    const [newState, newFilterOrder] = removeAllFilters();
    setFilterState(newState);
    setFilterOrder(newFilterOrder);
  };

  const getColumnFilters = () => {
    return toColumnFilters(filterState);
  };

  const initialize = (filters: ColumnFilter[]) => {
    const newFilterState = initializeFilterState(filters);
    setFilterState(newFilterState);
    setFilterOrder(Object.keys(newFilterState));
  };

  return {
    addFilter,
    removeFilter,
    changeFilterColumn,
    changeFilterValue,
    reset,
    filterState,
    filterOrder,
    getColumnFilters,
    initialize,
  };
};

export default useDataGridFilterMenuState;
